import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_gtm_83e2476e from 'nuxt_plugin_gtm_83e2476e' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_65d3434a from 'nuxt_plugin_cookieuniversalnuxt_65d3434a' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_axios_5242f69a from 'nuxt_plugin_axios_5242f69a' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_pluginrouting_6a0f8d9a from 'nuxt_plugin_pluginrouting_6a0f8d9a' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_26cf771c from 'nuxt_plugin_pluginmain_26cf771c' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_metaplugin_52d36122 from 'nuxt_plugin_metaplugin_52d36122' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_577b0696 from 'nuxt_plugin_iconplugin_577b0696' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_directives_521c0486 from 'nuxt_plugin_directives_521c0486' // Source: ../plugins/directives.js (mode: 'all')
import nuxt_plugin_axios_5659cf26 from 'nuxt_plugin_axios_5659cf26' // Source: ../plugins/axios.ts (mode: 'all')
import nuxt_plugin_routerextend_e0a4e2e2 from 'nuxt_plugin_routerextend_e0a4e2e2' // Source: ../plugins/router-extend.js (mode: 'all')
import nuxt_plugin_routerreferral_c989aaa8 from 'nuxt_plugin_routerreferral_c989aaa8' // Source: ../plugins/router-referral.js (mode: 'all')
import nuxt_plugin_filters_2dd71148 from 'nuxt_plugin_filters_2dd71148' // Source: ../plugins/filters.ts (mode: 'all')
import nuxt_plugin_extendcontext_19a789c5 from 'nuxt_plugin_extendcontext_19a789c5' // Source: ../plugins/extend-context.js (mode: 'all')
import nuxt_plugin_authentication_d7e6b602 from 'nuxt_plugin_authentication_d7e6b602' // Source: ../plugins/authentication.ts (mode: 'all')
import nuxt_plugin_validators_68c66f14 from 'nuxt_plugin_validators_68c66f14' // Source: ../plugins/validators.ts (mode: 'all')
import nuxt_plugin_mq_341c8d5a from 'nuxt_plugin_mq_341c8d5a' // Source: ../plugins/mq.ts (mode: 'client')
import nuxt_plugin_notifications_15a88cbb from 'nuxt_plugin_notifications_15a88cbb' // Source: ../plugins/notifications.ts (mode: 'client')
import nuxt_plugin_vuecarousel3d_e42043be from 'nuxt_plugin_vuecarousel3d_e42043be' // Source: ../plugins/vue-carousel-3d (mode: 'client')
import nuxt_plugin_auth_6ecf7bc6 from 'nuxt_plugin_auth_6ecf7bc6' // Source: ./auth.js (mode: 'all')
import nuxt_plugin_authi18n_db8450c0 from 'nuxt_plugin_authi18n_db8450c0' // Source: ../plugins/auth-i18n.ts (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"C-Patex","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"author","name":"author","content":"https:\u002F\u002Fc-patex.com"},{"hid":"publisher","name":"publisher","content":"https:\u002F\u002Fc-patex.com"},{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"C-Patex Exchange"},{"hid":"theme-color","name":"theme-color","content":"#FFFFFF"},{"hid":"description","name":"description","content":"Latin American platform for crypto exchange, crypto education and career guidance around the world"},{"hid":"og:title","property":"og:title","content":"C-Patex Exchange"},{"hid":"og:description","property":"og:description","content":"Latin American platform for crypto exchange, crypto education and career guidance around the world"},{"hid":"og:type","property":"og:type","content":"website"},{"hid":"og:url","property":"og:url","content":"https:\u002F\u002Fc-patex.com"},{"hid":"og:image","property":"og:image","content":"https:\u002F\u002Fc-patex.com\u002Fimage\u002Fog-image-new.png"},{"hid":"og:image:alt","property":"og:image:alt","content":"C-Patex Exchange page cover"},{"hid":"og:locale","property":"og:locale","content":"en"},{"hid":"og:site_name","property":"og:site_name","content":""},{"hid":"og:image:width","property":"og:image:width","content":"749"},{"hid":"og:image:height","property":"og:image:height","content":"400"},{"hid":"twitter:card","name":"twitter:card","content":"summary_large_image"},{"hid":"twitter:site","name":"twitter:site","content":"twitter"},{"hid":"twitter:creator","name":"twitter:creator","content":"twitter"},{"hid":"twitter:title","name":"twitter:title","content":"C-Patex Exchange"},{"hid":"twitter:description","name":"twitter:description","content":"Latin American platform for crypto exchange, crypto education and career guidance around the world"},{"hid":"twitter:image","name":"twitter:image","content":"https:\u002F\u002Fc-patex.com\u002Fimage\u002Fog-image-new.png"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"\u002Fdatafeeds\u002Fudf\u002Fdist\u002Fbundle.js"},{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);}})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"style":[],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":""}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_gtm_83e2476e === 'function') {
    await nuxt_plugin_gtm_83e2476e(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_65d3434a === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_65d3434a(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_5242f69a === 'function') {
    await nuxt_plugin_axios_5242f69a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_6a0f8d9a === 'function') {
    await nuxt_plugin_pluginrouting_6a0f8d9a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_26cf771c === 'function') {
    await nuxt_plugin_pluginmain_26cf771c(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_52d36122 === 'function') {
    await nuxt_plugin_metaplugin_52d36122(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_577b0696 === 'function') {
    await nuxt_plugin_iconplugin_577b0696(app.context, inject)
  }

  if (typeof nuxt_plugin_directives_521c0486 === 'function') {
    await nuxt_plugin_directives_521c0486(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_5659cf26 === 'function') {
    await nuxt_plugin_axios_5659cf26(app.context, inject)
  }

  if (typeof nuxt_plugin_routerextend_e0a4e2e2 === 'function') {
    await nuxt_plugin_routerextend_e0a4e2e2(app.context, inject)
  }

  if (typeof nuxt_plugin_routerreferral_c989aaa8 === 'function') {
    await nuxt_plugin_routerreferral_c989aaa8(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2dd71148 === 'function') {
    await nuxt_plugin_filters_2dd71148(app.context, inject)
  }

  if (typeof nuxt_plugin_extendcontext_19a789c5 === 'function') {
    await nuxt_plugin_extendcontext_19a789c5(app.context, inject)
  }

  if (typeof nuxt_plugin_authentication_d7e6b602 === 'function') {
    await nuxt_plugin_authentication_d7e6b602(app.context, inject)
  }

  if (typeof nuxt_plugin_validators_68c66f14 === 'function') {
    await nuxt_plugin_validators_68c66f14(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_mq_341c8d5a === 'function') {
    await nuxt_plugin_mq_341c8d5a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_notifications_15a88cbb === 'function') {
    await nuxt_plugin_notifications_15a88cbb(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuecarousel3d_e42043be === 'function') {
    await nuxt_plugin_vuecarousel3d_e42043be(app.context, inject)
  }

  if (typeof nuxt_plugin_auth_6ecf7bc6 === 'function') {
    await nuxt_plugin_auth_6ecf7bc6(app.context, inject)
  }

  if (typeof nuxt_plugin_authi18n_db8450c0 === 'function') {
    await nuxt_plugin_authi18n_db8450c0(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    const { route } = router.resolve(app.context.route.fullPath)
    // Ignore 404s rather than blindly replacing URL
    if (!route.matched.length && process.client) {
      return resolve()
    }
    router.replace(route, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
